/**
 * `useConfirm()` hook + module-level helper.
 *
 * Two call shapes are supported to make porting from the monolith easy:
 *
 *   // Hook (preferred in components):
 *   const confirm = useConfirm();
 *   confirm({
 *     title: 'Delete drive',
 *     message: 'Are you sure?',
 *     confirmText: 'Delete',
 *     type: 'danger',
 *     onConfirm: () => doDelete()
 *   });
 *
 *   // Legacy signature (matches monolith showConfirmModal):
 *   showConfirmModal('Delete drive', 'Are you sure?', 'Delete', () => doDelete(), 'danger')
 *
 * The legacy form delegates to the same store action so behaviour is
 * identical.
 */

import { useUiStore } from '@/state/uiStore.js';

/**
 * @typedef {import('@/state/uiStore.js').ConfirmType} ConfirmType
 */

/**
 * @typedef {Object} ConfirmOptions
 * @property {string} title
 * @property {string} message
 * @property {string} [confirmText]
 * @property {ConfirmType} [type]
 * @property {() => void} onConfirm
 */

/**
 * Monolith-style helper. Works outside React.
 *
 * @param {string} title
 * @param {string} message
 * @param {string} [confirmText]
 * @param {() => void} [callback]
 * @param {ConfirmType} [type]
 */
export function showConfirmModal(title, message, confirmText = 'Confirm', callback, type = 'warning') {
  useUiStore.getState().openConfirm({
    title,
    message,
    confirmText,
    type,
    onConfirm: callback ?? (() => {})
  });
}

/**
 * React hook entry point.
 *
 * @returns {(opts: ConfirmOptions) => void}
 */
export function useConfirm() {
  const openConfirm = useUiStore((s) => s.openConfirm);
  return openConfirm;
}
